import { StrictTimeStringHHMM, StrictTimeStringHHMMSS } from '@/components/FormFields/types';

function HHMMSSIntoNumber(
  str: StrictTimeStringHHMMSS | StrictTimeStringHHMM | null
): number | null {
  if (!str) return null;
  const [hours, minutes, seconds] = str.split(':').map(Number);
  return hours * 3600 + minutes * 60 + (seconds || 0);
}

function NumberIntoHHMMSS(num: number): StrictTimeStringHHMMSS {
  const hours = Math.floor(num / 3600);
  const minutes = Math.floor((num % 3600) / 60);
  const sec = num % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(sec).padStart(2, '0')}` as StrictTimeStringHHMMSS;
}

function NumberIntoHHMM(num: number): StrictTimeStringHHMM {
  const hours = Math.floor(num / 3600);
  const minutes = Math.floor((num % 3600) / 60);
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}` as StrictTimeStringHHMM;
}

function NumberIntoMMSS(num: number): string {
  const minutes = Math.floor(num / 60);
  const sec = num % 60;
  return `${String(minutes).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

function convertISOToSeconds(isoString: string, referenceTime: Date): number {
  const date = new Date(isoString);
  return (date.getTime() - referenceTime.getTime()) / 1000;
}

/**
 * Absolute datetime of the frame currently shown in the player:
 * `base` (segment/clip `start_datetime`) + `playerVideoCurrentTime` seconds,
 * rounded to the nearest whole second.
 *
 * The whole-second rounding is the single source of truth that keeps the
 * persisted `start_datetime`/`end_datetime` and the "Tiempo absoluto del
 * programa" shown on screen in sync: because the stored instant lands exactly
 * on a second (0 ms), every render path agrees on the value regardless of
 * whether it rounds (the absolute-time field) or truncates via `getSeconds()`
 * (segment/clip lists). Sub-second precision would reintroduce the RCOMM-5972
 * desync, where the field rounded a `.6s` instant up while the stored value
 * was truncated down, showing a 1s gap between what was shown and saved.
 *
 * Replaces the legacy `newDateTime.setSeconds(newDateTime.getSeconds() + t)`
 * pattern, which both dropped the fractional part of `playerVideoCurrentTime`
 * AND anchored inconsistently against the base's sub-second offset.
 */
function getAbsoluteDateFromPlayerTime(base: Date | string, playerVideoCurrentTime: number): Date {
  const baseMs = (typeof base === 'string' ? new Date(base) : base).getTime();
  const absoluteMs = baseMs + playerVideoCurrentTime * 1000;
  return new Date(Math.round(absoluteMs / 1000) * 1000);
}

function convertDatesToSeconds(from?: Date | string, to?: Date | string): number {
  if (!from || !to) throw new Error('Both from and to dates are required');
  if (typeof from === 'string') {
    from = new Date(from);
  }
  if (typeof to === 'string') {
    to = new Date(to);
  }
  return (from.getTime() - to.getTime()) / 1000;
}

function getOffsetHHMMSS(targetTimeISO: string, referenceTimeISO: string): StrictTimeStringHHMMSS {
  const reference = new Date(referenceTimeISO);
  const seconds = convertISOToSeconds(targetTimeISO, reference);
  return NumberIntoHHMMSS(Math.ceil(seconds));
}

export {
  convertISOToSeconds,
  convertDatesToSeconds,
  getAbsoluteDateFromPlayerTime,
  getOffsetHHMMSS,
  HHMMSSIntoNumber,
  NumberIntoHHMM,
  NumberIntoHHMMSS,
  NumberIntoMMSS,
};
